<template>
  <div>
    <button
      class="btn btn--outline border-gray-800 text-gray-800 md:hidden flex items-center justify-center gap-2"
      type="button"
      @click="openMenu"
    >
      <IconAdjustmentsHorizontal :aria-hidden="true" />
      <span v-text="'Filter'" />
      <span
        class="sr-only"
        v-text="' Course Results'"
      />
    </button>
  </div>

  <Dialog
    :initial-focus="mobileFilterMenuTitleRef"
    :open="dialogOpen"
    :unmount="false"
    as="div"
    class="relative z-50 lg:hidden"
    @close="closeMenu"
  >
    <transition
      enter-active-class="ease-in-out duration-500 motion-reduce:transition-none"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="ease-in-out duration-500 motion-reduce:transition-none"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
      @after-leave="dialogOpen = false"
    >
      <div
        v-show="menuOpen"
        class="fixed inset-0 bg-gray-500/75 transition-opacity"
      />
    </transition>

    <div class="fixed inset-0 overflow-hidden">
      <div class="absolute inset-0 overflow-hidden">
        <div class="pointer-events-none fixed inset-y-0 left-0 flex max-w-full pr-10">
          <transition
            enter-active-class="transition-transform ease-out duration-300 motion-reduce:hover:transform-none"
            enter-from-class="-translate-x-full"
            enter-to-class="translate-x-0"
            leave-active-class="transition-transform ease-in duration-300 motion-reduce:hover:transform-none"
            leave-from-class="translate-x-0"
            leave-to-class="-translate-x-full"
          >
            <DialogPanel
              v-show="menuOpen"
              class="pointer-events-auto w-screen max-w-md"
            >
              <div class="flex h-full flex-col overflow-y-auto bg-white pb-6 shadow-sm">
                <div class="flex justify-between items-center border-b border-b-gray-300">
                  <DialogTitle
                    ref="mobileFilterMenuTitleRef"
                    class="heading-sm pl-4"
                    tabindex="-1"
                    v-text="'Filters'"
                  />
                  <button
                    class="wc-focus relative flex size-12 items-center justify-center rounded-full focus-visible:outline-offset-1"
                    type="button"
                    @click="menuOpen = false"
                  >
                    <span class="absolute inset-px" />
                    <span
                      class="sr-only"
                      v-text="'Close Course Filters Menu'"
                    />
                    <IconX
                      :size="24"
                      aria-hidden="true"
                    />
                  </button>
                </div>
                <div
                  v-for="refinement in refinements"
                  :key="refinement.attribute"
                  class="px-4"
                >
                  <div class="py-3 space-y-4">
                    <h3
                      class="heading-xs"
                      v-text="refinement.label"
                    />

                    <AisRefinementList :attribute="refinement.attribute">
                      <template #default="{ items, refine }: { items: AisRefinementListItem[], refine: (value: string) => void }">
                        <ul class="space-y-4">
                          <li
                            v-for="item in items"
                            :key="item.value"
                          >
                            <div class="flex items-center gap-x-3">
                              <input
                                :id="item.value"
                                :checked="item.isRefined"
                                :name="item.value"
                                type="checkbox"
                                @change="handleChange(refine, item)"
                              >
                              <label
                                :for="item.value"
                                class="font-normal capitalize"
                                v-text="item.label"
                              />
                            </div>
                          </li>
                        </ul>
                      </template>
                    </AisRefinementList>
                  </div>
                </div>

                <div class="p-4 flex flex-row md:flex-col justify-between gap-x-4 border-t border-gray-100 md:border-none mt-auto">
                  <button
                    class="btn btn--primary md:hidden grow"
                    type="button"
                    @click="closeMenu"
                  >
                    <span v-text="'Apply'" />
                    <span
                      class="sr-only"
                      v-text="' Search Filters'"
                    />
                  </button>

                  <AisClearRefinements
                    :class-names="{
                      'ais-ClearRefinements-button': 'btn btn--outline max-lg:hidden',
                    }"
                  >
                    <template #resetLabel>
                      Clear Filters
                    </template>
                  </AisClearRefinements>
                </div>
              </div>
            </DialogPanel>
          </transition>
        </div>
      </div>
    </div>
  </Dialog>
</template>

<script lang="ts" setup>
import { ref } from 'vue'
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/vue'
import { IconAdjustmentsHorizontal, IconX } from '@tabler/icons-vue'
import { AisClearRefinements, AisRefinementList } from 'vue-instantsearch/vue3/es'

import { AisRefinementListItem } from '@js/types/search/AisRefinementListItem'

defineOptions({
  name: 'MobileCourseFilters',
})

const props = defineProps<{
  searchClient: any | null
}>()

const refinements = [
  {
    attribute: 'primary_category',
    label: 'Course',
  },
  {
    attribute: 'course_type',
    label: 'Format',
  },
  {
    attribute: 'location',
    label: 'Location',
  },
]

const menuOpen = ref(false)
const dialogOpen = ref(false)
const mobileFilterMenuTitleRef = ref<HTMLHeadingElement | null>(null)

const openMenu = (): void => {
  menuOpen.value = true
  dialogOpen.value = true
}

const closeMenu = (): void => {
  menuOpen.value = false
}

const handleChange = (refine: (value: string) => void, item: AisRefinementListItem): void => {
  refine(item.value)

  // Belt and braces on freshness for AppCalendarSearch's grouped data.
  props.searchClient?.clearCache()
}
</script>
